"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
import { DEACTIVATE_DP_ENUM } from "@/lib/constants/dp-enum";
import cloudflareLoader from "@/lib/utils/cdn-cloudflare-loader";
import Image from "next/image";
const FLIP_DELAY_MS = 500;
const FLIP_BACK_DELAY_MS = 1000;
const FLIP_DURATION_MS = 1000;
const SWIPE_THRESHOLD_PX = 30;
function prefersReducedMotion() {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}
export default function ProfilePhotoFlip(t0) {
  const $ = _c(35);
  const {
    logo,
    profilePicture,
    status
  } = t0;
  const [flipped, setFlipped] = useState(false);
  const [introComplete, setIntroComplete] = useState(false);
  const pointerStartRef = useRef(null);
  const didSwipeRef = useRef(false);
  const isActive = status !== DEACTIVATE_DP_ENUM;
  const hasLogo = Boolean(logo) && isActive;
  const hasProfile = Boolean(profilePicture) && isActive;
  const shouldFlip = hasLogo && hasProfile;
  let t1;
  let t2;
  if ($[0] !== shouldFlip) {
    t1 = () => {
      if (!shouldFlip) {
        return;
      }
      if (prefersReducedMotion()) {
        const timer = setTimeout(() => setIntroComplete(true), 0);
        return () => clearTimeout(timer);
      }
      let flipBackTimer;
      let introCompleteTimer;
      const flipToProfileTimer = setTimeout(() => {
        setFlipped(true);
        flipBackTimer = setTimeout(() => {
          setFlipped(false);
          introCompleteTimer = setTimeout(() => setIntroComplete(true), FLIP_DURATION_MS);
        }, FLIP_BACK_DELAY_MS);
      }, FLIP_DELAY_MS);
      return () => {
        clearTimeout(flipToProfileTimer);
        clearTimeout(flipBackTimer);
        clearTimeout(introCompleteTimer);
      };
    };
    t2 = [shouldFlip];
    $[0] = shouldFlip;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== introComplete) {
    t3 = event => {
      if (!introComplete) {
        return;
      }
      pointerStartRef.current = {
        x: event.clientX,
        y: event.clientY
      };
      didSwipeRef.current = false;
    };
    $[3] = introComplete;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const handlePointerDown = t3;
  let t4;
  if ($[5] !== introComplete) {
    t4 = event_0 => {
      if (!introComplete || !pointerStartRef.current) {
        return;
      }
      const deltaX = event_0.clientX - pointerStartRef.current.x;
      const deltaY = event_0.clientY - pointerStartRef.current.y;
      pointerStartRef.current = null;
      const isHorizontalSwipe = Math.abs(deltaX) > SWIPE_THRESHOLD_PX && Math.abs(deltaX) > Math.abs(deltaY);
      if (!isHorizontalSwipe) {
        return;
      }
      didSwipeRef.current = true;
      setFlipped(_temp);
      event_0.stopPropagation();
      event_0.preventDefault();
    };
    $[5] = introComplete;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const handlePointerUp = t4;
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = event_1 => {
      if (didSwipeRef.current) {
        event_1.stopPropagation();
        didSwipeRef.current = false;
      }
    };
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const handleClick = t5;
  if (!isActive || !hasLogo && !hasProfile) {
    let t6;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <div className="w-[100px] h-[100px] rounded-xl overflow-hidden relative mr-3 border-[2px] border-[--colorDisabledOnColorDisabled]"><Image src="/images/empty-dp-img.png" className="rounded-xl object-cover" fill={true} sizes="100px" alt="display picture" quality={80} priority={true} /></div>;
      $[8] = t6;
    } else {
      t6 = $[8];
    }
    return t6;
  }
  if (!shouldFlip) {
    const imageSrc = hasProfile ? profilePicture : logo;
    const isRound = hasProfile && !hasLogo;
    const t6 = `w-[100px] h-[100px] ${isRound ? "rounded-full" : "rounded-lg"} overflow-hidden relative mr-3 border-[2px] border-[--colorDisabledOnColorDisabled]`;
    let t7;
    if ($[9] !== imageSrc) {
      t7 = cloudflareLoader({
        src: imageSrc,
        width: 250,
        quality: 75
      });
      $[9] = imageSrc;
      $[10] = t7;
    } else {
      t7 = $[10];
    }
    const t8 = `${isRound ? "rounded-full" : "rounded-lg"} object-cover`;
    const t9 = isRound ? "profile picture" : "business logo";
    let t10;
    if ($[11] !== t7 || $[12] !== t8 || $[13] !== t9) {
      t10 = <Image src={t7} className={t8} alt={t9} fill={true} sizes="100px" priority={true} />;
      $[11] = t7;
      $[12] = t8;
      $[13] = t9;
      $[14] = t10;
    } else {
      t10 = $[14];
    }
    let t11;
    if ($[15] !== t10 || $[16] !== t6) {
      t11 = <div className={t6}>{t10}</div>;
      $[15] = t10;
      $[16] = t6;
      $[17] = t11;
    } else {
      t11 = $[17];
    }
    return t11;
  }
  let t6;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      pointerStartRef.current = null;
    };
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const t7 = `relative w-full h-full transition-transform duration-1000 ease-in-out [transform-style:preserve-3d] ${flipped ? "[transform:rotateY(180deg)]" : ""}`;
  let t8;
  if ($[19] !== logo) {
    t8 = cloudflareLoader({
      src: logo,
      width: 250,
      quality: 75
    });
    $[19] = logo;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== t8) {
    t9 = <div className="absolute inset-0 [backface-visibility:hidden] rounded-lg overflow-hidden border-[2px] border-[--colorDisabledOnColorDisabled]"><Image src={t8} className="rounded-lg object-cover" alt="business logo" fill={true} sizes="100px" priority={true} /></div>;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== profilePicture) {
    t10 = cloudflareLoader({
      src: profilePicture,
      width: 250,
      quality: 75
    });
    $[23] = profilePicture;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== t10) {
    t11 = <div className="absolute inset-0 [backface-visibility:hidden] [transform:rotateY(180deg)] rounded-full overflow-hidden border-[2px] border-[--colorDisabledOnColorDisabled]"><Image src={t10} className="rounded-full object-cover" alt="profile picture" fill={true} sizes="100px" priority={true} /></div>;
    $[25] = t10;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== t11 || $[28] !== t7 || $[29] !== t9) {
    t12 = <div className={t7}>{t9}{t11}</div>;
    $[27] = t11;
    $[28] = t7;
    $[29] = t9;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== handlePointerDown || $[32] !== handlePointerUp || $[33] !== t12) {
    t13 = <div className="w-[100px] h-[100px] relative mr-3 [perspective:1000px] touch-pan-y" onPointerDown={handlePointerDown} onPointerUp={handlePointerUp} onPointerCancel={t6} onClick={handleClick}>{t12}</div>;
    $[31] = handlePointerDown;
    $[32] = handlePointerUp;
    $[33] = t12;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  return t13;
}
function _temp(prev) {
  return !prev;
}